import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import { ArrowLeft, ImageIcon, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SHIPPING_QUEUE_META, SHIPMENT_EVENT_LABELS, fmtIDR } from "@/lib/constants";
import { NumInput } from "@/components/NumInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const CAN_MANAGE = ["owner", "admin_input"];

function applyRounding(kg, rule) {
  if (rule === "ceil_half_kg") return Math.ceil(kg * 2) / 2;
  if (rule === "none") return kg;
  return Math.ceil(kg);
}

const EMPTY_FORM = { actual_weight_kg: "", chargeable_weight_kg: "", courier: "", shipping_charge: "", insured: true, awb: "", notes: "", address: "" };

export default function ShippingDetail() {
  const { orderId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);
  const [job, setJob] = useState(null);
  const [settings, setSettings] = useState(null);
  const [subtotal, setSubtotal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirmShip, setConfirmShip] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const [authorizing, setAuthorizing] = useState(false);
  const [authReason, setAuthReason] = useState("");
  const [gateBlocked, setGateBlocked] = useState(false);
  const [busy, setBusy] = useState(false);

  const canManage = CAN_MANAGE.includes(user.role);
  const isOwner = user.role === "owner";
  const showMoney = user.role !== "viewer";
  const shipment = entry?.shipment || null;
  const qstatus = entry?.queue_status;
  const editable = canManage && qstatus !== "shipped";

  const load = useCallback(async () => {
    try {
      const [{ data: queue }, { data: jobs }, { data: st }] = await Promise.all([
        api.get("/shipping/queue"),
        api.get("/packing/jobs", { params: { order_id: orderId, status: "packed" } }),
        api.get("/settings"),
      ]);
      const e = queue.find((x) => x.order_id === orderId);
      if (!e) {
        toast.error("This order is not in the shipping queue");
        navigate("/shipping");
        return;
      }
      setEntry(e);
      setJob(jobs[0] || null);
      setSettings(st);
      if (e.shipment) {
        setForm({
          actual_weight_kg: String(e.shipment.actual_weight_kg ?? ""),
          chargeable_weight_kg: String(e.shipment.chargeable_weight_kg ?? ""),
          courier: e.shipment.courier || "",
          shipping_charge: e.shipment.shipping_charge != null ? String(e.shipment.shipping_charge) : "",
          insured: e.shipment.insured !== false,
          awb: e.shipment.awb || "",
          notes: e.shipment.notes || "",
          address: e.shipment.address || "",
        });
      }
      if (user.role !== "viewer") {
        try {
          const { data: o } = await api.get(`/orders/${orderId}`);
          setSubtotal(o.items.filter((i) => i.status === "active").reduce((s, i) => s + (i.selling_price != null ? i.selling_price * i.quantity : 0), 0));
        } catch {
          setSubtotal(null);
        }
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [orderId, navigate, user.role]);

  useEffect(() => {
    load();
  }, [load]);

  if (!entry) {
    return (
      <div className="grid place-items-center py-24" data-testid="shipping-detail-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const meta = SHIPPING_QUEUE_META[qstatus] || SHIPPING_QUEUE_META.ready;
  const insuranceEstimate = showMoney && form.insured && subtotal != null ? Math.round((subtotal * (settings?.insurance_pct ?? 0)) / 100) : null;
  const saveValid = Number(form.actual_weight_kg) > 0 && Number(form.chargeable_weight_kg) > 0 && form.courier.trim() && form.shipping_charge !== "" && Number(form.shipping_charge) >= 0;

  const setActual = (v) => {
    const chargeable = v !== "" && Number(v) > 0 ? String(applyRounding(Number(v), settings?.weight_rounding || "ceil_kg")) : "";
    setForm({ ...form, actual_weight_kg: v, chargeable_weight_kg: chargeable });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/shipping/orders/${orderId}/details`, {
        actual_weight_kg: Number(form.actual_weight_kg),
        chargeable_weight_kg: Number(form.chargeable_weight_kg),
        courier: form.courier.trim(),
        shipping_charge: Number(form.shipping_charge),
        insured: form.insured,
        awb: form.awb.trim() || null,
        notes: form.notes.trim() || null,
        address: form.address.trim() || null,
      });
      toast.success("Shipping details saved — SHIPPING PREPARED");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const markShipped = async () => {
    if (!confirmShip) {
      setConfirmShip(true);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/mark-shipped`, { awb: form.awb.trim() || null });
      toast.success("Marked as SHIPPED — record locked");
      setConfirmShip(false);
      setGateBlocked(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
      if (String(e?.response?.data?.detail || "").includes("Balance due")) setGateBlocked(true);
      setConfirmShip(false);
    } finally {
      setBusy(false);
    }
  };

  const reopen = async () => {
    if (!reopenReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/reopen`, { reason: reopenReason });
      toast.success("Shipment reopened — SHIPPING PREPARED");
      setReopening(false);
      setReopenReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const authorizeUnpaid = async () => {
    if (!authReason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/shipments/${shipment.id}/authorize-unpaid`, { reason: authReason });
      toast.success("Ship-before-payment authorized");
      setAuthorizing(false);
      setAuthReason("");
      setGateBlocked(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="shipping-detail-page">
      <button
        data-testid="back-to-shipping"
        onClick={() => navigate("/shipping")}
        className="mb-3 flex h-10 items-center gap-1 text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Shipping
      </button>

      <div className="mb-4 rounded-md border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="font-heading text-2xl font-extrabold tracking-tight" data-testid="shipping-customer-name">{entry.customer_name}</h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Order #{entry.order_number} · {entry.batch_name}
            </p>
          </div>
          <span data-testid="shipping-status-badge" className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>{meta.label}</span>
        </div>
        {entry.packed_at && (
          <p className="mt-2 text-xs text-muted-foreground" data-testid="shipping-packed-info">
            {entry.packed_units} units packed · {format(new Date(entry.packed_at), "d MMM yyyy, HH:mm")}
          </p>
        )}
      </div>

      {job && (
        <div className="mb-4 rounded-md border bg-card p-4" data-testid="shipping-packed-items">
          <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Packed Items (read-only)</h2>
          <div className="space-y-2">
            {job.items.map((it) => (
              <div key={it.order_item_id} data-testid={`shipping-item-${it.order_item_id}`} className="flex items-center gap-3">
                {productImage(it) ? (
                  <img src={productImage(it)} alt={it.product_name} className="h-10 w-10 shrink-0 rounded-md border bg-stone-100 object-cover" />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                    <ImageIcon className="h-4 w-4 text-stone-300" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">{it.product_name}{it.variant ? ` (${it.variant})` : ""}</p>
                </div>
                <span className="shrink-0 rounded-md border bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700">×{it.qty_packed}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {editable && (
        <div className="rounded-md border bg-card p-4" data-testid="shipping-input-form">
          <h2 className="mb-3 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Shipping Input</h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <Label>Actual Weight (kg) *</Label>
                <Input data-testid="ship-actual-weight" type="number" min="0" step="0.1" className="h-12" value={form.actual_weight_kg} onChange={(e) => setActual(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Chargeable Weight (kg) *</Label>
                <Input data-testid="ship-chargeable-weight" type="number" min="0" step="0.5" className="h-12" value={form.chargeable_weight_kg} onChange={(e) => setForm({ ...form, chargeable_weight_kg: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Courier *</Label>
              <Input data-testid="ship-courier" className="h-12" placeholder="JNE, SiCepat, GoSend…" value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Shipping Cost (IDR) *</Label>
              <NumInput data-testid="ship-cost" mode="money" className="h-12" value={form.shipping_charge} onValue={(v) => setForm({ ...form, shipping_charge: v })} />
            </div>
            <div className="flex items-center justify-between rounded-md border bg-card px-3 py-2.5">
              <Label htmlFor="ship-insured" className="text-sm font-semibold">Insurance ({settings?.insurance_pct}% of order value)</Label>
              <Switch id="ship-insured" data-testid="ship-insured" checked={form.insured} onCheckedChange={(v) => setForm({ ...form, insured: v })} />
            </div>
            {insuranceEstimate != null && (
              <p className="text-xs text-muted-foreground" data-testid="ship-insurance-estimate">
                Insurance amount (auto): <span className="font-bold text-foreground">{fmtIDR(insuranceEstimate)}</span>
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Tracking Number / AWB</Label>
              <Input data-testid="ship-awb" className="h-12" value={form.awb} onChange={(e) => setForm({ ...form, awb: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Shipping Note</Label>
              <Textarea data-testid="ship-note" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Address</Label>
              <Textarea data-testid="ship-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
        </div>
      )}

      {!editable && shipment && (
        <div className="rounded-md border bg-card p-4" data-testid="shipping-readonly-summary">
          <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Shipping Details</h2>
          <div className="space-y-1 text-xs">
            <p className="flex justify-between"><span className="text-muted-foreground">Courier</span><span className="font-semibold">{shipment.courier || "—"}</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">Weight</span><span className="font-semibold">{shipment.actual_weight_kg} kg → {shipment.chargeable_weight_kg} kg</span></p>
            <p className="flex justify-between"><span className="text-muted-foreground">AWB</span><span className="font-semibold">{shipment.awb || "—"}</span></p>
            {showMoney && (
              <>
                <p className="flex justify-between"><span className="text-muted-foreground">Shipping cost</span><span className="font-bold" data-testid="ship-cost-value">{fmtIDR(shipment.shipping_charge ?? 0)}</span></p>
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Insurance</span>
                  <span className="font-semibold">{shipment.insured ? fmtIDR(shipment.insurance_amount ?? 0) : `No (cap ${fmtIDR(shipment.uninsured_compensation_cap ?? 0)})`}</span>
                </p>
              </>
            )}
            {shipment.notes && <p className="flex justify-between"><span className="text-muted-foreground">Note</span><span className="font-semibold">{shipment.notes}</span></p>}
          </div>
        </div>
      )}

      {qstatus === "shipped" && (
        <p className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-800" data-testid="shipped-banner">
          Shipped by {shipment.shipped_by_name} · {format(new Date(shipment.shipped_at), "d MMM yyyy, HH:mm")} — record locked
        </p>
      )}

      {shipment?.unpaid_override && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs" data-testid="ship-override-banner">
          <p className="font-bold text-red-700">SHIP-BEFORE-PAYMENT authorized</p>
          <p className="mt-0.5 text-red-600">
            by {shipment.unpaid_override.authorized_by}
            {shipment.unpaid_override.outstanding_amount != null && ` · outstanding ${fmtIDR(shipment.unpaid_override.outstanding_amount)}`}
            {` · ${shipment.unpaid_override.reason}`}
          </p>
        </div>
      )}

      {editable && (
        <div className="sticky bottom-20 z-30 mt-4 space-y-2 rounded-md border bg-white/95 p-3 backdrop-blur md:bottom-4" data-testid="shipping-action-bar">
          <Button data-testid="save-shipping-button" className="h-12 w-full text-base" onClick={save} disabled={busy || !saveValid}>
            Save Shipping Details
          </Button>
          {shipment && (
            <Button
              data-testid="mark-shipped-button"
              className={`h-12 w-full text-base ${confirmShip ? "bg-emerald-600 hover:bg-emerald-700" : ""}`}
              onClick={markShipped}
              disabled={busy}
            >
              {confirmShip ? "Confirm shipment? This will lock the shipping record." : "Mark as Shipped"}
            </Button>
          )}
        </div>
      )}

      {editable && isOwner && shipment && gateBlocked && !shipment.unpaid_override && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid="authorize-unpaid-section">
          {!authorizing ? (
            <Button variant="outline" data-testid="authorize-unpaid-button" className="h-11 w-full" onClick={() => setAuthorizing(true)}>
              Owner: Authorize ship-before-payment
            </Button>
          ) : (
            <div className="space-y-2">
              <Input data-testid="authorize-unpaid-reason" className="h-11 bg-white" placeholder="Reason (required)" value={authReason} onChange={(e) => setAuthReason(e.target.value)} />
              <div className="flex gap-2">
                <Button variant="outline" className="h-11 flex-1" data-testid="authorize-unpaid-cancel" onClick={() => setAuthorizing(false)}>
                  Cancel
                </Button>
                <Button data-testid="authorize-unpaid-confirm" className="h-11 flex-1" onClick={authorizeUnpaid} disabled={busy || !authReason.trim()}>
                  Authorize
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {qstatus === "shipped" && isOwner && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-4" data-testid="shipping-reopen-section">
          {!reopening ? (
            <Button variant="outline" data-testid="shipping-reopen-button" className="h-11 w-full" onClick={() => setReopening(true)}>
              Owner: Reopen this shipment
            </Button>
          ) : (
            <div className="space-y-2">
              <Input
                data-testid="shipping-reopen-reason"
                className="h-11 bg-white"
                placeholder="Reason for reopening (required)"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
              />
              <div className="flex gap-2">
                <Button variant="outline" className="h-11 flex-1" data-testid="shipping-reopen-cancel" onClick={() => setReopening(false)}>
                  Cancel
                </Button>
                <Button data-testid="shipping-reopen-confirm" className="h-11 flex-1" onClick={reopen} disabled={busy || !reopenReason.trim()}>
                  Confirm Reopen
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {shipment?.audit?.length > 0 && (
        <div className="mt-5" data-testid="shipping-history">
          <h2 className="mb-2 font-heading text-sm font-bold uppercase tracking-wide text-muted-foreground">Shipping History</h2>
          <div className="space-y-1.5">
            {[...shipment.audit].reverse().map((e, i) => (
              <div key={i} className="rounded-md border bg-card p-3">
                <p className="text-xs font-semibold">{SHIPMENT_EVENT_LABELS[e.event] || e.event}</p>
                <p className="text-[11px] text-muted-foreground">
                  {e.by_name} · {format(new Date(e.at), "d MMM yyyy, HH:mm")}
                  {e.details?.reason && ` · ${e.details.reason}`}
                  {e.details?.awb && ` · AWB ${e.details.awb}`}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {!shipment && qstatus === "ready" && (
        <p className="mt-4 flex items-center gap-2 rounded-md border border-dashed px-3 py-2.5 text-xs text-muted-foreground" data-testid="shipping-no-record">
          <Truck className="h-4 w-4 shrink-0" /> No shipping record yet — save shipping details to prepare this order.
        </p>
      )}
      <div className="h-6" />
    </div>
  );
}
